import { useState } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { VehicleTypeSimpleList } from '@/components/admin/VehicleTypeSimpleList';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ImageUpload } from '@/components/admin/ImageUpload';
import {
  useVehicleTypes,
  useCreateVehicleType,
  useUpdateVehicleType,
  useDeleteVehicleType,
  VehicleType
} from '@/hooks/useVehicleTypes';

const categoryLabels: Record<string, string> = {
  car: 'Carro',
  truck: 'Caminhão',
  motorcycle: 'Moto',
  bus: 'Ônibus',
  van: 'Van',
  other: 'Outro'
};

export default function VehicleTypesManager() {
  const { data: vehicleTypes, isLoading } = useVehicleTypes();
  const createMutation = useCreateVehicleType();
  const updateMutation = useUpdateVehicleType();
  const deleteMutation = useDeleteVehicleType();

  const [modalOpen, setModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<VehicleType | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    image_url: '',
    default_capacity: '',
    description: '',
    is_active: true
  });

  const handleOpenCreate = () => {
    setSelectedType(null);
    setFormData({
      name: '',
      image_url: '',
      default_capacity: '',
      description: '',
      is_active: true
    });
    setModalOpen(true);
  };

  const handleOpenEdit = (type: VehicleType) => {
    setSelectedType(type);
    setFormData({
      name: type.name,
      image_url: type.image_url || '',
      default_capacity: type.default_capacity?.toString() || '',
      description: type.description || '',
      is_active: type.is_active
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    const data = {
      name: formData.name,
      category: formData.name, // categoria sempre igual ao nome
      image_url: formData.image_url || undefined,
      default_capacity: formData.default_capacity ? parseFloat(formData.default_capacity) : undefined,
      description: formData.description || undefined,
      is_active: formData.is_active
    };

    if (selectedType) {
      await updateMutation.mutateAsync({ id: selectedType.id, ...data });
    } else {
      await createMutation.mutateAsync(data as any);
    }
    setModalOpen(false);
  };

  const handleToggleActive = (type: VehicleType) => {
    updateMutation.mutate({
      id: type.id,
      is_active: !type.is_active,
    });
  };

  const handleDelete = async () => {
    if (selectedType) {
      await deleteMutation.mutateAsync(selectedType.id);
      setDeleteDialogOpen(false);
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center p-8">Carregando...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Tipos de Veículos</h2>
          <p className="text-muted-foreground">Gerencie os tipos globais de veículos</p>
        </div>
        <Button onClick={handleOpenCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Tipo
        </Button>
      </div>

      <VehicleTypeSimpleList
        vehicleTypes={vehicleTypes || []}
        onEdit={handleOpenEdit}
        onDelete={(type) => {
          setSelectedType(type);
          setDeleteDialogOpen(true);
        }}
        onToggleActive={handleToggleActive}
      />

      {/* Modal de Criar/Editar */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {selectedType ? 'Editar Tipo de Veículo' : 'Novo Tipo de Veículo'}
            </DialogTitle>
            <DialogDescription>
              Preencha os dados do tipo de veículo
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nome *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Caminhão Toco"
              />
            </div>

            <div className="space-y-2">
              <Label>Imagem</Label>
              <ImageUpload
                value={formData.image_url}
                onChange={(url) => setFormData({ ...formData, image_url: url })}
                bucket="device-images"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="default_capacity">Capacidade Padrão</Label>
              <Input
                id="default_capacity"
                type="number"
                step="0.1"
                value={formData.default_capacity}
                onChange={(e) => setFormData({ ...formData, default_capacity: e.target.value })}
                placeholder="Ex: 6 (toneladas ou passageiros)"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descrição</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrição do tipo de veículo"
                rows={3}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label htmlFor="is_active">Tipo ativo</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!formData.name || createMutation.isPending || updateMutation.isPending}
            >
              {selectedType ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o tipo "{selectedType?.name}"?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
